/**
 * The technology associate's Today (1.5.0, role tech): their own tickets for today — the time, the tenant's first
 * name, the apartment or room, what they need help with, the device — with Call (the ticket's button; no number
 * shown), Open ticket, Arrived and Close out. A ticket not closed out after its end sits at the top as Pending
 * close-out. Nothing else: no People, no Admin, no other tenant.
 *
 * 1.8.5 (D-1.8.5-4): at the door, "Tenant is home — start" (the Arrived press) and beside it "Not home" — no entry,
 * ever; the ticket goes back to the office as Needs a new day. Close out comes after the start (for the tech; the
 * office may still record a late close-out). A ticket the tech has not opened yet says New (D-1.8.5-3). Workshops
 * moved to Tech Support → Workshops.
 */
import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatClock } from '@shared/dayplan';
import { serviceDateKey, serviceMinutesNow } from '@shared/dates';
import { helpList, type TechTicketView, type TechTodayResponse } from '@shared/tech';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { formatDayKey, greetingKey } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { CallButton } from '@/components/Contact';
import { Icon } from '@/components/Icons';
import { StatusBadge, appointmentStatusKey } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import type { Me } from '@/auth/useMe';
import { VisitMode } from './visit/VisitMode';
import { TechCloseoutSheet } from './TechCloseoutSheet';
import { formatClockWords } from './ticketWords';

export function useTechToday() {
  const day = serviceDateKey();
  return useQuery({ queryKey: ['tech-today', day], queryFn: () => apiFetch<TechTodayResponse>(`/api/tech/today?day=${day}`), refetchInterval: 60_000 });
}

/**
 * One ticket's facts and its presses (Today and the Open ticket page). `office`: the administrator, the
 * co-administrator or the scheduler is looking — they may record a late close-out of a visit never started.
 */
export function TicketCard({ ticket, full = false, office = false }: { ticket: TechTicketView; full?: boolean; office?: boolean }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const qc = useQueryClient();
  const [visiting, setVisiting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [askNotHome, setAskNotHome] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const notHomeBtn = useRef<HTMLButtonElement | null>(null);
  const notHome = useMutation({
    mutationFn: () => apiFetch(`/api/tech/tickets/${encodeURIComponent(ticket.appointmentId)}/not-home`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      setAskNotHome(false);
      announce(t('visit185.notHomeDone', { who: ticket.tenantFirst }));
      void qc.invalidateQueries();
    },
    onError: (e) => {
      setAskNotHome(false);
      announce(e instanceof ApiRequestError ? (e.body?.message ?? e.message) : String(e), 'assertive');
    },
  });
  const where = ticket.place === 'Apartment' ? t('techToday.apt', { unit: ticket.unit }) : t(`tech.place.${ticket.place}`);
  const status = appointmentStatusKey(ticket.status, ticket.confirmation);
  const open = ticket.status === 'Scheduled' || ticket.status === 'In Progress';
  // 1.6.8 (D-1.6.8-1): booked without a completed agreement — the card says so and the visit cannot start (the
  // server refuses the press too). Words and an icon, the buttons disabled and described by the line: never colour alone.
  const blocked = !!ticket.agreementMissing;
  const blockedId = useId();
  // 1.8.5 (D-1.8.5-4): nobody home — no entry, ever; the office finds a new day
  const wasNotHome = !!ticket.notHome;
  const started = ticket.status === 'In Progress' || !!ticket.arrivedAt;
  // the tech closes out only a visit he started with the tenant home
  const closeWaits = !office && !started;
  const startFirstId = useId();
  const atTheDoor = ticket.status === 'Scheduled' && ticket.dayKey === serviceDateKey() && !wasNotHome && !ticket.closeout;
  return (
    <li className="visit ticket" data-testid={`ticket-${ticket.appointmentId}`}>
      <div className="time tnum">
        {formatClock(ticket.minutes)}
        <small>
          {formatClock(ticket.minutes)}–{formatClock(ticket.endMinutes)}
        </small>
      </div>
      <div className="who">
        <div className="apt">
          {ticket.tenantFirst} <span>· {where}</span>
        </div>
        <div className="sub">
          <span>
            <b>{t('techToday.helpWith')}</b> {helpList(ticket.help, ticket.other, lang)}
          </span>
          <span>
            <b>{t('techToday.device')}</b> {ticket.device}
          </span>
          {full && ticket.description ? (
            <span>
              <b>{t('techToday.said')}</b> {ticket.description}
            </span>
          ) : null}
          {ticket.closeout ? <span data-testid={`ticket-closed-${ticket.appointmentId}`}>{t(`techClose.o.${ticket.closeout.outcome}`)}</span> : null}
        </div>
        {wasNotHome ? (
          <div className="warn" role="note" data-testid={`ticket-not-home-${ticket.appointmentId}`}>
            <Icon name="info" />
            <div>
              <b>{t('visit185.notHomeLine', { time: formatClockWords(ticket.notHome!.at, lang) })}</b>
              <span>{t('visit185.notHomeSub')}</span>
            </div>
          </div>
        ) : null}
        {blocked ? (
          <div className="warn tech168-block" role="note" id={blockedId} data-testid={`ticket-no-agreement-${ticket.appointmentId}`}>
            <Icon name="alert" />
            <div>
              <b>{t('tech168.notSigned')}</b>
              <span>{t('tech168.notSignedHelp')}</span>
            </div>
          </div>
        ) : null}
      </div>
      <div className="side">
        <div className="badges">
          {ticket.isNew ? (
            <span className="status prop has-icon" data-testid={`ticket-new-${ticket.appointmentId}`}>
              <Icon name="sparkle" />
              {t('visit185.new')}
            </span>
          ) : null}
          <StatusBadge status={status} />
          {ticket.awaitingAgreement ? (
            <span className="status hold has-icon" data-testid={`ticket-awaiting-agreement-${ticket.appointmentId}`}>
              <Icon name="clock" />
              {t('agreement.awaiting')}
            </span>
          ) : null}
        </div>
        <div className="row-actions">
          <CallButton phone={ticket.phone} name={ticket.tenantFirst} />
          {!full ? (
            <Link className="btn secondary sm" to={`/tech/${encodeURIComponent(ticket.appointmentId)}`} data-testid={`open-ticket-${ticket.appointmentId}`}>
              {t('attention.openTicket')}
            </Link>
          ) : null}
          {(atTheDoor || started) && !ticket.closeout && !wasNotHome ? (
            <Button size="sm" disabled={blocked} aria-describedby={blocked ? blockedId : undefined} aria-label={t(started ? 'visit190.resume' : 'visit190.start')} onClick={(e) => { opener.current = e.currentTarget; setVisiting(true); }} data-testid={`tech-arrived-${ticket.appointmentId}`}>
              {t(started ? 'visit190.resume' : 'visit190.start')}
            </Button>
          ) : null}
          {atTheDoor ? (
            <Button
              size="sm"
              variant="secondary"
              ref={notHomeBtn}
              disabled={notHome.isPending}
              aria-label={t('visit185.notHomeFor', { who: ticket.tenantFirst })}
              onClick={() => setAskNotHome(true)}
              data-testid={`tech-not-home-${ticket.appointmentId}`}
            >
              {t('visit185.notHome')}
            </Button>
          ) : null}
          {open && !ticket.closeout && !wasNotHome ? (
            <Button
              size="sm"
              variant={ticket.status === 'In Progress' ? undefined : 'secondary'}
              disabled={blocked || closeWaits}
              aria-describedby={blocked ? blockedId : closeWaits ? startFirstId : undefined}
              aria-label={t('techToday.closeFor', { who: ticket.tenantFirst })}
              onClick={(e) => {
                opener.current = e.currentTarget;
                setClosing(true);
              }}
              data-testid={`tech-close-${ticket.appointmentId}`}
            >
              {t('techToday.closeOut')}
            </Button>
          ) : null}
        </div>
        {closeWaits && open && !ticket.closeout && !wasNotHome && !blocked ? (
          <p className="help" id={startFirstId} data-testid={`ticket-start-first-${ticket.appointmentId}`}>
            {t('visit185.startFirst')}
          </p>
        ) : null}
      </div>
      {askNotHome ? (
        <Dialog open onOpenChange={(o) => !o && setAskNotHome(false)}>
          <DialogContent
            data-testid="confirm-not-home"
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              notHomeBtn.current?.focus();
            }}
          >
            <DialogTitle>{t('visit185.notHomeTitle', { who: ticket.tenantFirst })}</DialogTitle>
            <DialogDescription>{t('visit185.notHomeAsk')}</DialogDescription>
            <div className="chips">
              <Button type="button" variant="secondary" onClick={() => setAskNotHome(false)} disabled={notHome.isPending} data-testid="confirm-not-home-no">
                {t('visit185.notYet')}
              </Button>
              <Button busy={notHome.isPending} type="button" onClick={() => notHome.mutate()} disabled={notHome.isPending} data-testid="confirm-not-home-yes">
                {t('visit185.notHomeYes')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
      <Sheet open={visiting} onOpenChange={(o) => !o && setVisiting(false)}>{visiting ? <VisitMode ticket={ticket} returnFocusTo={opener} onDone={() => setVisiting(false)} /> : null}</Sheet>
      <Sheet open={closing} onOpenChange={(o) => !o && setClosing(false)}>
        {closing ? <TechCloseoutSheet ticket={ticket} returnFocusTo={opener} onDone={() => setClosing(false)} /> : null}
      </Sheet>
    </li>
  );
}

export function TechTodayPage({ me }: { me: Me }) {
  const { t, i18n } = useTranslation();
  const q = useTechToday();
  const tickets = q.data?.tickets ?? [];
  const now = serviceMinutesNow();
  const pending = tickets.filter((x) => !x.closeout && (x.status === 'Scheduled' || x.status === 'In Progress') && x.endMinutes <= now);
  return (
    <section aria-labelledby="h-today">
      <div className="page-head">
        <div>
          <div className="eyebrow">{formatDayKey(serviceDateKey(), i18n.language)}</div>
          <h1 id="h-today" tabIndex={-1}>
            {t(greetingKey())}, {me.name}
          </h1>
        </div>
      </div>
      <div className="warn" style={{ marginBottom: 14 }} data-testid="tech-note">
        <Icon name="info" />
        <div>{t('techToday.note')}</div>
      </div>
      {pending.length ? (
        <Card aria-labelledby="h-tech-pending" className="pending-card" data-testid="tech-pending">
          <CardHeader>
            <CardTitle id="h-tech-pending">
              <Icon name="alert" /> {t('today.pendingCloseOut', { count: pending.length })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="attn-list">
              {pending.map((x) => (
                <li key={x.appointmentId}>
                  <span className="dot" style={{ background: 'var(--st-attn-dot)' }} aria-hidden="true" />
                  <div className="t">
                    <b>
                      {x.tenantFirst} · {formatClock(x.minutes)}
                    </b>
                    <span>{t('techToday.pendingSub')}</span>
                  </div>
                  <Link className="btn secondary sm" to={`/tech/${encodeURIComponent(x.appointmentId)}`}>
                    {t('attention.openTicket')}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
      <Card aria-labelledby="h-tickets" aria-busy={q.isLoading}>
        <CardHeader>
          <CardTitle id="h-tickets">
            {t('techToday.title')}
            {q.data ? ` · ${tickets.length}` : ''}
          </CardTitle>
        </CardHeader>
        {q.isLoading ? (
          <CardContent>
            <p className="help">{t('app.loading')}</p>
          </CardContent>
        ) : q.isError ? (
          <CardContent>
            <div className="warn take" role="alert">
              <Icon name="alert" />
              <div>{(q.error as Error).message}</div>
            </div>
          </CardContent>
        ) : tickets.length === 0 ? (
          <div className="empty" data-testid="tech-empty">
            <Icon name="calendarEmpty" />
            <b>{t('techToday.none')}</b>
          </div>
        ) : (
          <ul aria-label={t('techToday.title')} data-testid="ticket-list">
            {tickets.map((x) => (
              <TicketCard key={x.appointmentId} ticket={x} />
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}
